import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ChargebackCaseSchema, ChargebackListItemSchema, TransactionEventSchema } from '../schemas';
import { z } from 'zod';
const apiBase = () => {
    const fromEnv = process.env.DEFENSE_REVIEW_API_BASE_URL;
    if (!fromEnv) {
        throw new Error('DEFENSE_REVIEW_API_BASE_URL is not configured');
    }
    return fromEnv.replace(/\/$/, '');
};
// Attach the Cognito session JWT to every API call. The list + per-case
// endpoints are authorized by an API Gateway Cognito authorizer. When the
// refresh token has expired we sign the operator out so the Amplify
// Authenticator surfaces the login screen instead of stranding the UI with an
// opaque "not authenticated" error.
const authHeader = async () => {
    try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) {
            await signOut();
            throw new Error('session expired');
        }
        return { Authorization: `Bearer ${idToken}` };
    }
    catch (err) {
        await signOut().catch(() => undefined);
        throw err instanceof Error ? err : new Error('session expired');
    }
};
const ChargebackListResponseSchema = z.object({
    cases: z.array(ChargebackListItemSchema)
});
const TransactionEventListResponseSchema = z.object({
    events: z.array(TransactionEventSchema)
});
const RegenerateResponseSchema = z.object({
    executionArn: z.string()
});
export const chargebackApi = createApi({
    reducerPath: 'chargebackApi',
    baseQuery: fetchBaseQuery({
        baseUrl: apiBase(),
        prepareHeaders: async (headers) => {
            const auth = await authHeader();
            Object.entries(auth).forEach(([key, value]) => headers.set(key, value));
            return headers;
        }
    }),
    tagTypes: ['ChargebackList', 'ChargebackCase', 'TransactionEvents'],
    endpoints: builder => ({
        getChargebackList: builder.query({
            query: () => '/chargeback-cases',
            transformResponse: (response) => ChargebackListResponseSchema.parse(response).cases,
            providesTags: ['ChargebackList']
        }),
        getChargebackCase: builder.query({
            query: ({ pspReference, version }) => {
                const query = version !== undefined ? `?version=${encodeURIComponent(version)}` : '';
                return `/chargeback-cases/${encodeURIComponent(pspReference)}${query}`;
            },
            transformResponse: (response) => ChargebackCaseSchema.parse(response),
            providesTags: (_result, _error, arg) => [
                { type: 'ChargebackCase', id: arg.pspReference }
            ]
        }),
        getTransactionEvents: builder.query({
            query: pspReference => `/chargeback-cases/${encodeURIComponent(pspReference)}/transaction-events`,
            transformResponse: (response) => TransactionEventListResponseSchema.parse(response).events,
            providesTags: (_result, _error, pspReference) => [
                { type: 'TransactionEvents', id: pspReference }
            ]
        }),
        submitReview: builder.mutation({
            query: ({ pspReference, decision, reviewerNote }) => ({
                url: `/chargeback-cases/${encodeURIComponent(pspReference)}/review`,
                method: 'POST',
                body: { decision, reviewerNote }
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'ChargebackCase', id: arg.pspReference },
                'ChargebackList'
            ]
        }),
        submitReservationResolution: builder.mutation({
            query: ({ pspReference, decision, reservationId, reviewerNote }) => ({
                url: `/chargeback-cases/${encodeURIComponent(pspReference)}/apaleo-details-resolution`,
                method: 'POST',
                body: { decision, reservationId, reviewerNote }
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'ChargebackCase', id: arg.pspReference },
                'ChargebackList'
            ]
        }),
        // Kicks off a regenerate execution. Returns 202 + executionArn; the new
        // version appears on the case after Bedrock + PDF render finish (typically
        // 30-60s). Caller polls getChargebackCase for the new version rather than
        // relying on tag invalidation.
        regenerateDefenseDocument: builder.mutation({
            query: ({ pspReference, operatorNote }) => ({
                url: `/chargeback-cases/${encodeURIComponent(pspReference)}/regenerate-defense`,
                method: 'POST',
                body: { operatorNote }
            }),
            transformResponse: (response) => {
                const parsed = RegenerateResponseSchema.parse(response);
                return { executionArn: parsed.executionArn };
            }
        })
    })
});
export const { useGetChargebackListQuery, useGetChargebackCaseQuery, useLazyGetChargebackCaseQuery, useGetTransactionEventsQuery, useSubmitReviewMutation, useSubmitReservationResolutionMutation, useRegenerateDefenseDocumentMutation } = chargebackApi;
